/**
 * Data layer for `affiliate-signup`. Thin typed fetch wrappers over /api/v1/affiliate-signup.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface AffiliateSignupFormConfig {
	tenant: { id: string; slug: string; name: string };
	branding: { brand_color: string; logo_url: string | null };
}

export interface AffiliateSignupCreateBody {
	name: string;
	email: string;
	company?: string;
	website?: string;
	traffic_sources?: string[];
	referrer?: string;
	branding?: { brand_color?: string; logo_url?: string };
	[key: string]: unknown;
}

export interface AffiliateSignupRow {
	id: string;
	status: string;
	tenant_id?: string;
	duplicate?: boolean;
	[key: string]: unknown;
}

const BASE = "/api/v1/affiliate-signup";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function fetchFormConfig(): Promise<AffiliateSignupFormConfig> {
	const res = await fetch(`${BASE}/form-config`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<AffiliateSignupFormConfig>(res);
}

export async function create(body: AffiliateSignupCreateBody): Promise<AffiliateSignupRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<AffiliateSignupRow>(res);
}

export const affiliateSignupQueryKeys = {
	all: ["affiliate-signup"] as const,
	list: () => [...affiliateSignupQueryKeys.all] as const,
	detail: (id: string) => [...affiliateSignupQueryKeys.all, id] as const,
	formConfig: () => [...affiliateSignupQueryKeys.all, "form-config"] as const,
};
